'use client'

import Link from 'next/link'
import { useState } from 'react'

type FormStatus = 'error' | 'idle' | 'sending' | 'success'
type FieldErrors = Record<string, string>

const FieldError = ({ id, message }: { id: string; message?: string }) =>
  message ? (
    <p className="contact-form__field-error" id={id} role="alert">
      {message}
    </p>
  ) : null

export const ContactForm = () => {
  const [errors, setErrors] = useState<FieldErrors>({})
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<FormStatus>('idle')

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    setStatus('sending')
    setMessage('')
    setErrors({})

    const formData = new FormData(form)
    const payload = {
      companyWebsite: String(formData.get('companyWebsite') || ''),
      consent: formData.get('consent') === 'on',
      email: String(formData.get('email') || ''),
      message: String(formData.get('message') || ''),
      name: String(formData.get('name') || ''),
      subject: String(formData.get('subject') || ''),
    }

    try {
      const response = await fetch('/api/contact', {
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
      const result = (await response.json().catch(() => ({}))) as {
        error?: string
        fieldErrors?: FieldErrors
        message?: string
      }

      if (!response.ok) {
        setErrors(result.fieldErrors || {})
        setMessage(result.error || 'The message could not be sent. Please try again.')
        setStatus('error')
        return
      }

      form.reset()
      setMessage(result.message || 'Thank you. Your message has been sent.')
      setStatus('success')
    } catch {
      setMessage('The message could not be sent. Check your connection and try again.')
      setStatus('error')
    }
  }

  return (
    <div className="contact-form-card">
      <div className="contact-form-card__heading">
        <p className="contact-form-card__eyebrow">Write to the team</p>
        <h2>Send a message</h2>
      </div>

      <form className="contact-form" onSubmit={submit}>
        <div
          className={`contact-form__status contact-form__status--${status}`}
          aria-live="polite"
          role="status"
        >
          {message}
        </div>

        <div className="contact-form__honeypot" aria-hidden="true">
          <label htmlFor="companyWebsite">Company website</label>
          <input
            autoComplete="off"
            id="companyWebsite"
            name="companyWebsite"
            tabIndex={-1}
            type="text"
          />
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-name">Name</label>
          <input
            aria-describedby={errors.name ? 'contact-name-error' : undefined}
            aria-invalid={Boolean(errors.name)}
            autoComplete="name"
            id="contact-name"
            maxLength={100}
            minLength={2}
            name="name"
            required
            type="text"
          />
          <FieldError id="contact-name-error" message={errors.name} />
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-email">Email</label>
          <input
            aria-describedby={errors.email ? 'contact-email-error' : undefined}
            aria-invalid={Boolean(errors.email)}
            autoComplete="email"
            id="contact-email"
            maxLength={254}
            name="email"
            required
            type="email"
          />
          <FieldError id="contact-email-error" message={errors.email} />
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-subject">Subject</label>
          <input
            aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
            aria-invalid={Boolean(errors.subject)}
            id="contact-subject"
            maxLength={150}
            minLength={3}
            name="subject"
            required
            type="text"
          />
          <FieldError id="contact-subject-error" message={errors.subject} />
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-message">Message</label>
          <textarea
            aria-describedby={errors.message ? 'contact-message-error' : undefined}
            aria-invalid={Boolean(errors.message)}
            id="contact-message"
            maxLength={5_000}
            minLength={20}
            name="message"
            required
            rows={8}
          />
          <FieldError id="contact-message-error" message={errors.message} />
        </div>

        <label className="contact-form__consent" htmlFor="contact-consent">
          <input
            aria-describedby={errors.consent ? 'contact-consent-error' : undefined}
            aria-invalid={Boolean(errors.consent)}
            id="contact-consent"
            name="consent"
            required
            type="checkbox"
          />
          <span>
            I have read the <Link href="/privacy-policy">Privacy Policy</Link> and agree that
            WhyManhattan may use this information to respond to my message.
          </span>
        </label>
        <FieldError id="contact-consent-error" message={errors.consent} />

        <button disabled={status === 'sending'} type="submit">
          {status === 'sending' ? 'Sending…' : 'Send message'}
        </button>
        <p className="contact-form__hint">
          Please do not include passwords, payment details, medical information, or other sensitive
          data.
        </p>
      </form>
    </div>
  )
}
